import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';

import { isEqual } from 'lodash-es';
import { combineLatest, defer, Observable, of } from 'rxjs';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';

import { SharedLanguageService } from '@language';
import {
    ExtendedFacetProfileField,
    FacetProfile,
    FacetProfileField,
    FieldDefinition,
    FieldType,
    QueryPartData,
    QueryPartStrategy,
    ShFacet,
    ShSelectedFacetLabelService,
    ShSelectedFacetValue,
    ShUrlQueryService,
    TranslateDateRangeQuery,
    VirtualFacetField
} from '@stylehaven/components';
import { ActiveFilterPill, FacetFilterPill } from '../components/active-filter-bar/entities/active-filter-pill.entity';
import { Column } from '../entities/column.entity';
import { Comparable } from '../entities/misc/comparable.interface';
import { Trackable } from '../entities/misc/trackable.interface';

/** Everything one active-filter bar is built from, in one argument. */
export interface ActiveFilterPillsInput<T extends Comparable & Trackable> {
    /** The `f` state, straight from {@link ShUrlQueryService.getSelectedFacets}. */
    selectedFacets: ShSelectedFacetValue[];
    facetProfile: FacetProfile;
    fieldDefinitions: FieldDefinition[];
    /** The `cf` state, straight from {@link ActiveFilterStateService.getColumnFilters}. */
    columnFilters: QueryPartData[];
    /** The columns the table shows, which is how a `cf` field gets the label its header carries. */
    columns: Column<T>[];
    /**
     * HFBD-6: the facet whose values form one drill-down chain, which gets one pill instead of one per
     * segment. Absent — as on a Taken page, which has no submap — means no chain pill.
     */
    submapFacetKey?: string;
    /**
     * Descriptors for filters no facet profile lists, such as the Taken panel's *Mijn taken*. Without one,
     * such a filter yields no pill and one logged error per emission.
     */
    virtualFields?: readonly VirtualFacetField[];
}

/**
 * The page's own half of one active-filter bar. The `f` and `cf` state is not here, because the service
 * reads both itself — see {@link ActiveFilterStateService.getPills$}.
 */
export interface ActiveFilterPillSources<T extends Comparable & Trackable> {
    facetProfile$: Observable<FacetProfile>;
    fieldDefinitions$: Observable<FieldDefinition[]>;
    /** Absent while a page's table carries no column filters yet, which is how it yields no column pills. */
    columns$?: Observable<Column<T>[]>;
    /** See {@link ActiveFilterPillsInput.submapFacetKey}. */
    submapFacetKey?: string;
    /** See {@link ActiveFilterPillsInput.virtualFields}. */
    virtualFields?: readonly VirtualFacetField[];
}

/**
 * The one builder of active-filter pills, and the owner of the column-filter (`cf`) state they are partly
 * built from.
 *
 * Every surface that shows a pill bar goes through here, so a filter cannot be labelled or removed one way
 * on one page and another way on the next. The `cf` state lives here for the same reason: its read is the
 * exact call the builder already makes, and two owners of one URL parameter drift.
 */
@Injectable({ providedIn: 'root' })
export class ActiveFilterStateService {
    /**
     * The `cf` state. `defer` keeps the read lazy, so it happens when a caller first asks rather than when
     * this singleton is constructed.
     */
    private readonly columnFilters$: Observable<QueryPartData[]> = defer(() =>
        this.urlQueryService.getColumnFilterQuery()
    ).pipe(distinctUntilChanged(isEqual), shareReplay({ bufferSize: 1, refCount: true }));

    constructor(
        private readonly urlQueryService: ShUrlQueryService,
        private readonly selectedFacetLabelService: ShSelectedFacetLabelService,
        private readonly translateService: SharedLanguageService,
        private readonly datePipe: DatePipe
    ) {}

    /** The column-filter (`cf`) state. */
    getColumnFilters(): Observable<QueryPartData[]> {
        return this.columnFilters$;
    }

    /**
     * Sets one column's filter, replacing what that field held. An empty value removes the field.
     *
     * The other fields are read straight off the URL rather than from a cached copy, so a caller that only
     * writes — and never subscribes to {@link getColumnFilters} — cannot wipe them.
     */
    setColumnFilter(field: string, value: string): void {
        const current: QueryPartData[] = this.urlQueryService.$urlQueryState().structuredQuery.ColumnFilters;
        const withoutField: QueryPartData[] = current.filter(queryPart => queryPart.field !== field);
        const updated: QueryPartData[] = value
            ? [...withoutField, { field, strategy: QueryPartStrategy.MatchAll, values: [value] }]
            : withoutField;

        this.urlQueryService.setColumnFilterQuery(updated);
    }

    /**
     * The pills of one bar, as a stream. Every surface wires its bar this way rather than assembling the
     * five sources itself, so a page cannot end up reading a different `f` or `cf` than the one the service
     * writes back to.
     */
    getPills$<T extends Comparable & Trackable>(sources: ActiveFilterPillSources<T>): Observable<ActiveFilterPill[]> {
        return combineLatest([
            this.urlQueryService.getSelectedFacets(),
            sources.facetProfile$,
            sources.fieldDefinitions$,
            this.getColumnFilters(),
            sources.columns$ ?? of<Column<T>[]>([])
        ]).pipe(
            map(([selectedFacets, facetProfile, fieldDefinitions, columnFilters, columns]) =>
                this.toPills({
                    selectedFacets,
                    facetProfile,
                    fieldDefinitions,
                    columnFilters,
                    columns,
                    submapFacetKey: sources.submapFacetKey,
                    virtualFields: sources.virtualFields
                })
            ),
            distinctUntilChanged(isEqual),
            // refCount, so a destroyed container takes its subscription to the URL state with it. The
            // callers' `takeUntil` only completes what is downstream of here.
            shareReplay({ bufferSize: 1, refCount: true })
        );
    }

    /**
     * One pill per active filter: facet-origin (primary) first, then column-origin (secondary).
     *
     * The pure builder behind {@link getPills$}, and the seam its spec covers directly — no caller outside
     * this class builds pills without the URL state {@link getPills$} reads.
     */
    toPills<T extends Comparable & Trackable>(input: ActiveFilterPillsInput<T>): ActiveFilterPill[] {
        return [...this.toFacetPills(input), ...this.toColumnPills(input)];
    }

    /** Removes one active-filter pill: facet-origin clears its facet value; column-origin clears its column. */
    removePill(pill: ActiveFilterPill): void {
        if (pill.origin === 'facet') {
            // HFBD-6: one atomic write for a pill standing for a whole chain, so the query is never left
            // naming a folder without its parents. See `FacetFilterPill.clearsFacet`.
            if (pill.clearsFacet) {
                this.urlQueryService.replaceFacetValues({ key: pill.facetValue.key } as ShFacet, []);
            } else {
                this.urlQueryService.removeFacetValue(pill.facetValue);
            }
        } else {
            this.setColumnFilter(pill.field, '');
        }
    }

    /** Clears every active filter at once — both facet (`f`) and column (`cf`) state. */
    clearAll(): void {
        this.urlQueryService.clearFacetQuery();
    }

    /**
     * Builds the facet (primary) pills — one pill per selected facet value, labelled with the same translated
     * `facetLabel: valueLabel` the side panel shows (via the shared label service).
     */
    private toFacetPills<T extends Comparable & Trackable>({
        selectedFacets,
        facetProfile,
        fieldDefinitions,
        submapFacetKey,
        virtualFields = []
    }: ActiveFilterPillsInput<T>): ActiveFilterPill[] {
        const profileFields: ExtendedFacetProfileField[] = this.toExtendedFacetProfileFields(
            facetProfile.Fields ?? [],
            fieldDefinitions
        );

        const translated: ShSelectedFacetValue[] = this.selectedFacetLabelService.translateSelectedFacetLabels(
            selectedFacets,
            profileFields,
            virtualFields
        );

        // HFBD-6: the submap chain is one filter with several values, so it gets one pill, not one per segment.
        const submapPill: FacetFilterPill | null = this.toSubmapPill(translated, submapFacetKey);

        return [
            // First, deliberately: it is the filter that says *where* the user is, so it leads the bar.
            ...(submapPill ? [submapPill] : []),
            ...translated
                .filter(facet => submapPill === null || facet.key !== submapFacetKey)
                .sort((a, b) => a.facetLabel.localeCompare(b.facetLabel) || a.valueLabel.localeCompare(b.valueLabel))
                .map(facet => ({
                    origin: 'facet' as const,
                    label: `${facet.facetLabel}: ${facet.valueLabel}`,
                    facetValue: facet
                }))
        ];
    }

    /**
     * HFBD-6: one pill for a whole submap selection, naming the path it drilled down to.
     *
     * The segments are kept in the order the `f` parameter holds them, which is the order the tree emitted
     * them — ancestors first — so the label reads as the path it is.
     */
    private toSubmapPill(translated: ShSelectedFacetValue[], submapFacetKey?: string): FacetFilterPill | null {
        if (!submapFacetKey) {
            return null;
        }

        const chain: ShSelectedFacetValue[] = translated.filter(facet => facet.key === submapFacetKey);

        if (chain.length === 0) {
            return null;
        }

        return {
            origin: 'facet',
            label: `${chain[0].facetLabel}: ${chain.map(facet => facet.valueLabel).join(' / ')}`,
            facetValue: chain[0],
            clearsFacet: true
        };
    }

    /** Attaches each facet field's FieldDefinition by flat DottedKey, matching how enum/boolean columns resolve. */
    private toExtendedFacetProfileFields(
        fields: FacetProfileField[],
        fieldDefinitions: FieldDefinition[]
    ): ExtendedFacetProfileField[] {
        return fields.map(
            field =>
                ({
                    ...field,
                    FieldDefinition: fieldDefinitions.find(
                        fieldDefinition => fieldDefinition.DottedKey === field.DottedKey
                    )
                }) as ExtendedFacetProfileField
        );
    }

    /**
     * Builds the column (secondary) pills from the `cf` state — one per column filter. Text columns read
     * "<column label> bevat '<value>'"; date columns render the humanised range via
     * {@link TranslateDateRangeQuery}.
     *
     * A `cf` field is a column's `key`, which for some columns is the flat metadata key while its field
     * definition is keyed by `DottedKey` — so we resolve each filter against the paired {@link Column} list
     * (the same `key`/`fieldDefinition` pairing the table header uses) and fall back to matching a field
     * definition by either `FlatKey` or `DottedKey` for a filter whose column is not currently shown.
     *
     * The pill label mirrors the column header exactly: the column's own `label` when set, otherwise the
     * field definition's `Label` (its `Translations[locale]`, matching the `translateFieldDefinition` pipe).
     *
     * A column that declares its own filter fields (HFBD-115's "Naam en serie") is looked up first and by
     * field, because neither fallback can name those: the `cf` field is a real metadata field rather than
     * that column's key, so the column lookup misses it and the field definition's own label — "Naam object"
     * for the title — is not the wording the popover's input carries.
     */
    private toColumnPills<T extends Comparable & Trackable>({
        columnFilters,
        columns,
        fieldDefinitions
    }: ActiveFilterPillsInput<T>): ActiveFilterPill[] {
        const declaredLabels: Map<string, string> = new Map(
            columns.flatMap(candidate =>
                candidate.columnDefinition.filterFields.map(
                    filterField => [filterField.field, filterField.label] as const
                )
            )
        );

        return columnFilters.map(columnFilter => {
            const column: Column<T> | undefined = columns.find(
                candidate => candidate.columnDefinition.key === columnFilter.field
            );
            const fieldDefinition: FieldDefinition | undefined =
                column?.fieldDefinition ??
                fieldDefinitions.find(
                    definition =>
                        definition.FlatKey === columnFilter.field || definition.DottedKey === columnFilter.field
                );
            const fieldLabel: string =
                declaredLabels.get(columnFilter.field) ||
                column?.columnDefinition.label ||
                fieldDefinition?.Label ||
                columnFilter.field;
            const value: string = columnFilter.values[0] ?? '';
            const isDate: boolean = fieldDefinition?.Type === FieldType.DateField;

            return {
                origin: 'column' as const,
                field: columnFilter.field,
                label: isDate
                    ? `${fieldLabel}: ${this.toDateRangeLabel(value)}`
                    : `${fieldLabel} ${this.translateService.instant('active-filter-bar.contains')} '${value}'`
            };
        });
    }

    private toDateRangeLabel(value: string): string {
        return TranslateDateRangeQuery(
            value,
            this.translateService.instant('components.filter.date.from'),
            this.translateService.instant('components.filter.date.until'),
            this.datePipe
        );
    }
}
